import React, { useEffect, useState } from 'react';
import { Steps, Card, Button, Flex, Result, Spin, Typography } from 'antd';
import { LeftOutlined, RightOutlined, ReloadOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Header from '../components/Header/Header';
import Footer from '../components/Footer/Footer';
import { fetchJob } from '../../store/slices/lspivSlice';
import { clrs } from '../../constants/colors';
import classes from './lspiv.module.css';

import UploadStep from './steps/UploadStep';
import RectifyStep from './steps/RectifyStep';
import SectionsStep from './steps/SectionsStep';
import PivStep from './steps/PivStep';
import ResultsStep from './steps/ResultsStep';

const { Title, Text } = Typography;

const STEPS = [
    { key: 'upload',   title: 'Видео и кадры' },
    { key: 'rectify',  title: 'Ректификация' },
    { key: 'sections', title: 'Сечения и маски' },
    { key: 'piv',      title: 'PIV-анализ' },
    { key: 'results',  title: 'Результаты' },
];

const STEP_COMPONENTS = {
    upload: UploadStep,
    rectify: RectifyStep,
    sections: SectionsStep,
    piv: PivStep,
    results: ResultsStep,
};

const LSPIVWizardPage = () => {
    const { jobId } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { currentJob, isLoading } = useSelector((s) => s.lspiv);
    const [stepIndex, setStepIndex] = useState(0);

    useEffect(() => {
        if (jobId) dispatch(fetchJob(jobId));
    }, [dispatch, jobId]);

    const refresh = () => dispatch(fetchJob(jobId));

    if (isLoading && !currentJob) {
        return (
            <div>
                <Header />
                <Flex align="center" justify="center" style={{ minHeight: '100vh' }}>
                    <Spin size="large" />
                </Flex>
                <Footer />
            </div>
        );
    }

    if (!currentJob) {
        return (
            <div>
                <Header />
                <div className={classes.page}>
                    <Result
                        status="404"
                        title="Задание не найдено"
                        subTitle={`Job ${jobId} не существует или было удалено`}
                        extra={<Button type="primary" onClick={() => navigate('/lspiv')}>К списку</Button>}
                    />
                </div>
                <Footer />
            </div>
        );
    }

    const StepComponent = STEP_COMPONENTS[STEPS[stepIndex].key];
    const status = currentJob.status?.status;
    const step = currentJob.status?.step;

    return (
        <div>
            <Header />
            <div className={classes.page}>
                <div className={classes.wrapper}>
                    <Flex justify="space-between" align="center" wrap="wrap" gap={12} style={{ marginBottom: 16 }}>
                        <div>
                            <Title level={3} style={{ margin: 0, color: clrs.textColor }}>
                                Задание #{jobId}
                            </Title>
                            <Text type="secondary">
                                Статус: <b>{status || 'created'}</b>
                                {step && <> · шаг сервера: <b>{step}</b></>}
                            </Text>
                        </div>
                        <Flex gap={8}>
                            <Button icon={<ReloadOutlined />} onClick={refresh}>Обновить</Button>
                            <Button onClick={() => navigate('/lspiv')}>К списку</Button>
                        </Flex>
                    </Flex>

                    <Card className={classes.card} bodyStyle={{ padding: '24px' }}>
                        <Steps
                            current={stepIndex}
                            onChange={setStepIndex}
                            items={STEPS.map((s) => ({ title: s.title }))}
                            className={classes.stepsContainer}
                        />

                        <div style={{ paddingTop: 16 }}>
                            <StepComponent
                                jobId={jobId}
                                job={currentJob}
                                onRefresh={refresh}
                            />
                        </div>

                        <Flex justify="space-between" style={{ marginTop: 24 }}>
                            <Button
                                icon={<LeftOutlined />}
                                disabled={stepIndex === 0}
                                onClick={() => setStepIndex((i) => Math.max(0, i - 1))}
                            >
                                Назад
                            </Button>
                            <Button
                                type="primary"
                                disabled={stepIndex === STEPS.length - 1}
                                onClick={() => setStepIndex((i) => Math.min(STEPS.length - 1, i + 1))}
                            >
                                Далее <RightOutlined />
                            </Button>
                        </Flex>
                    </Card>
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default LSPIVWizardPage;
